<template>
  <SoDialog
    v-model="value"
    card-title="Manage bill delivery methods"
    :max-width="600"
    data-testid="manage-bill-delivery-modal"
  >
    <SoToggle
      v-model="paperBillsEnabled"
      label="Paper bills"
      name="paperBills"
      data-testid="manage-bill-delivery-toggle"
      :disabled="updatePaperBillMutator.isPending"
    />

    <SoAlert
      v-if="showNoEmailError"
      type="error"
      class="mt-4"
      title="Cannot remove paper bills from a customer with no email we can deliver bills to. Please add an email address to the primary contact and try again."
      data-testid="manage-bill-delivery-no-email-error"
    />

    <SoAlert
      v-if="updatePaperBillMutator.isError"
      type="error"
      class="mt-4"
      title="Failed to update bill delivery. Please try again."
      data-testid="manage-bill-delivery-error"
    />

    <div class="d-flex justify-end mt-8">
      <SoButton
        title="Cancel"
        class="mr-2"
        large
        :disabled="updatePaperBillMutator.isPending"
        @click="closeModal"
      />
      <SoButton
        title="Save"
        type="success"
        large
        data-testid="manage-bill-delivery-save-btn"
        :disabled="
          !hasUnsavedChanges ||
          showNoEmailError ||
          current.paperBillState.isFetching
        "
        :loading="updatePaperBillMutator.isPending"
        @click="save"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { PaperBillMutator } from "@/mutators/paperBill/PaperBillMutator"

  export default defineComponent({
    components: {
      SoDialog,
      SoAlert,
      SoButton,
      SoToggle,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        snackbar: useSnackbarStore(),
        updatePaperBillMutator: PaperBillMutator.updatePaperBill(),
        paperBillsEnabled: false,
      }
    },
    computed: {
      value: {
        get(): boolean {
          return this.modelValue
        },
        set(val: boolean) {
          this.$emit("update:modelValue", val)
        },
      },
      initialPaperBillsEnabled(): boolean {
        return this.current.paperBillState.data?.paperBillType != null
      },
      hasUnsavedChanges(): boolean {
        return this.paperBillsEnabled !== this.initialPaperBillsEnabled
      },
      primaryContactEmail(): string {
        const primary = this.current.accountContacts.data?.find(
          (contact) => contact.juniferPrimaryContact,
        )
        return primary?.email ?? ""
      },
      showNoEmailError(): boolean {
        return (
          !this.paperBillsEnabled &&
          this.initialPaperBillsEnabled &&
          !this.primaryContactEmail
        )
      },
      billingAccountId(): number | null {
        return this.current.coreId.data ?? null
      },
    },
    watch: {
      value: {
        immediate: true,
        handler(open: boolean) {
          if (open) {
            this.paperBillsEnabled = this.initialPaperBillsEnabled
            this.updatePaperBillMutator.reset()
          }
        },
      },
    },
    methods: {
      closeModal() {
        this.value = false
      },
      async save() {
        if (!this.billingAccountId) return
        if (this.showNoEmailError) return

        try {
          await this.updatePaperBillMutator.mutateAsync({
            billingAccountId: this.billingAccountId,
            enabled: this.paperBillsEnabled,
          })
          await this.current.paperBillState.refetch()
          this.snackbar.addSuccess(
            "Successfully updated bill delivery methods. It may take some time for the changes to take effect.",
          )
          this.closeModal()
        } catch {
          // Error surfaced via mutator.isError + in-modal alert
        }
      },
    },
  })
</script>
